/**
 * CancelBookingPage — public unauthenticated cancellation form
 * (subdomain rebuild §3.B.6).
 *
 * Reachable at `/cancel/:token`. Wired in `router.tsx` outside the
 * ProtectedRoute wrapper so an anonymous customer never gets bounced
 * to /login.
 *
 * Four states (matching the brief):
 *   - LOADING   : initial GET /api/v1/public/bookings/cancel/:token
 *   - FORM      : valid token + booking details + reason textarea
 *   - SUCCESS   : POST acknowledged
 *   - ERROR     : invalid / expired / already-cancelled / network error
 *
 * Copy mirrors the legacy `booking_cancellations/cancel_booking.php`
 * page wherever the wording is user-facing.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios, { type AxiosError } from 'axios';

import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Textarea } from '../../../components/form/Textarea';
import { endpoints } from '../../../api/endpoints';
import styles from './CancelBookingPage.module.css';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

interface CancelView {
  booking_reference: string;
  customer_name: string;
  vehicle_registration: string;
  company_name: string;
  company_logo_url: string | null;
  already_cancelled: boolean;
  expires_at: string;
}

interface SuccessResult {
  success: true;
  booking_reference: string;
}

type ScreenState =
  | { kind: 'loading' }
  | { kind: 'form'; view: CancelView }
  | { kind: 'already'; view: CancelView }
  | { kind: 'success'; bookingRef: string }
  | { kind: 'error'; message: string };

const REASON_MAX = 500;
const REASON_MIN = 10;

export function CancelBookingPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [state, setState] = useState<ScreenState>({ kind: 'loading' });
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (token.length === 0) {
        setState({
          kind: 'error',
          message: 'This cancellation link is invalid or expired.',
        });
        return;
      }
      try {
        const res = await axios.get<{ success: true; data: CancelView }>(
          `${API_BASE}${endpoints.publicBookings.cancel(token)}`,
        );
        if (cancelled) return;
        const view = res.data.data;
        if (view.already_cancelled) {
          setState({ kind: 'already', view });
        } else {
          setState({ kind: 'form', view });
        }
      } catch (err) {
        if (cancelled) return;
        setState({ kind: 'error', message: messageFor(err) });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setReasonError(null);
    const trimmed = reason.trim();
    if (trimmed.length < REASON_MIN) {
      setReasonError(
        `Please tell us a bit more — at least ${REASON_MIN} characters.`,
      );
      return;
    }
    if (trimmed.length > REASON_MAX) {
      setReasonError(`Reason is too long (max ${REASON_MAX} characters).`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post<{ success: true; data: SuccessResult }>(
        `${API_BASE}${endpoints.publicBookings.cancel(token)}`,
        { reason: trimmed },
      );
      setState({
        kind: 'success',
        bookingRef: res.data.data.booking_reference,
      });
    } catch (err) {
      const status = (err as AxiosError | undefined)?.response?.status;
      if (status === 409) {
        // Either the token's already used or the booking moved out of
        // a cancellable state in the meantime. Either way the customer
        // sees the same "already submitted" panel.
        setState({
          kind: 'already',
          view: state.kind === 'form' ? state.view : currentViewFallback(token),
        });
        return;
      }
      setReasonError(messageFor(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.shell}>
      <div className={styles.card} role="region" aria-label="Cancel booking">
        {state.kind === 'loading' ? (
          <div className={styles.spinnerWrap}>
            <Spinner />
          </div>
        ) : null}

        {state.kind === 'form' || state.kind === 'already' ? (
          <CompanyHeader view={state.view} />
        ) : null}

        {state.kind === 'form' ? (
          <>
            <h1 className={styles.title}>Cancel Booking</h1>
            <p className={styles.subtitle}>
              Please tell us why you need to cancel. Our team will review and
              confirm your cancellation by email.
            </p>
            <BookingDetail view={state.view} />
            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.field}>
                <Textarea
                  label="Reason for cancellation"
                  required
                  rows={5}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={REASON_MAX}
                  placeholder="Please tell us why you need to cancel this booking…"
                  error={reasonError ? { message: reasonError } : undefined}
                  data-testid="reason-input"
                />
                <div className={styles.fieldHint}>
                  <span>
                    {reason.length}/{REASON_MAX} characters (minimum{' '}
                    {REASON_MIN})
                  </span>
                  <span aria-hidden>Required</span>
                </div>
              </div>
              <div className={styles.actions}>
                <Button
                  type="submit"
                  variant="danger"
                  size="lg"
                  loading={submitting}
                  data-testid="submit-cancel"
                >
                  Request Cancellation
                </Button>
              </div>
            </form>
            <HelpFooter />
          </>
        ) : null}

        {state.kind === 'already' ? (
          <>
            <h1 className={styles.title}>Cancellation already submitted</h1>
            <div
              className={styles.alreadyPanel}
              data-testid="already-cancelled"
            >
              This cancellation request has already been submitted. Our team
              will be in touch shortly with the next steps.
            </div>
            <BookingDetail view={state.view} />
            <HelpFooter />
          </>
        ) : null}

        {state.kind === 'success' ? (
          <>
            <h1 className={styles.title}>Cancellation request received</h1>
            <div className={styles.successPanel} data-testid="success-panel">
              Your cancellation request has been received. Our team will be in
              touch shortly with the next steps.
            </div>
            <p className={styles.subtitle}>
              Booking reference: <strong>{state.bookingRef}</strong>
            </p>
            <HelpFooter />
          </>
        ) : null}

        {state.kind === 'error' ? (
          <>
            <h1 className={styles.title}>Link no longer valid</h1>
            <div className={styles.errorPanel} data-testid="error-panel">
              {state.message}
            </div>
            <HelpFooter />
          </>
        ) : null}
      </div>
    </div>
  );
}

function CompanyHeader({ view }: { view: CancelView }) {
  return (
    <div className={styles.brand}>
      {view.company_logo_url !== null && view.company_logo_url.length > 0 ? (
        <img
          className={styles.logo}
          src={view.company_logo_url}
          alt={`${view.company_name} logo`}
        />
      ) : null}
      <span className={styles.companyName}>{view.company_name}</span>
    </div>
  );
}

function BookingDetail({ view }: { view: CancelView }) {
  return (
    <dl className={styles.detailGrid}>
      <dt className={styles.detailLabel}>Reference</dt>
      <dd className={styles.detailValue}>{view.booking_reference}</dd>
      <dt className={styles.detailLabel}>Vehicle</dt>
      <dd className={styles.detailValue}>{view.vehicle_registration}</dd>
      <dt className={styles.detailLabel}>Customer</dt>
      <dd className={styles.detailValue}>{view.customer_name}</dd>
    </dl>
  );
}

function HelpFooter() {
  return (
    <p className={styles.helpFooter}>
      Need help? Please contact our office for assistance — keep your booking
      reference handy.
    </p>
  );
}

function messageFor(err: unknown): string {
  const ax = err as AxiosError | undefined;
  const status = ax?.response?.status;
  if (status === 404) {
    return 'This cancellation link is invalid or expired. Please contact our office for assistance.';
  }
  if (status === 410) {
    return 'This cancellation link has already been used.';
  }
  if (status === 429) {
    return 'Too many attempts on this link. Please try again in a few minutes.';
  }
  if (status === 400) {
    return 'Some of the details you submitted are not valid. Please review and try again.';
  }
  return 'Something went wrong while reaching the server. Please try again shortly.';
}

function currentViewFallback(token: string): CancelView {
  // Defensive default for the rare race where the GET resolved into the
  // "form" state but a parallel actor (or replay) flipped the booking
  // before our POST landed. The page only needs the company-facing
  // metadata to render the "already submitted" panel, so we degrade
  // gracefully.
  return {
    booking_reference: '—',
    customer_name: '—',
    vehicle_registration: '—',
    company_name: '',
    company_logo_url: null,
    already_cancelled: true,
    expires_at: new Date().toISOString(),
  };
  void token;
}
